<template>
    <div class="main-content literature-portal">
        <div class="flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
            <div>
                <h2 class="mb-1">Literature Portal</h2>
                <p class="mb-0 text-sm text-pip-gray">
                    Review literature details, availability, and publishing destinations.
                </p>
            </div>
            <router-link
                class="btn btn-sm btn-danger self-start md:self-auto"
                :to="{ name: 'literature-create' }"
            >
                New Literature
            </router-link>
        </div>
        <p class="diagonal-lines my-4"></p>
        <div v-if="deleteSuccessMessage" class="alert alert-success py-2" role="status">
            {{ deleteSuccessMessage }}
        </div>
        <div v-if="deleteError" class="alert alert-danger py-2" role="alert">
            {{ deleteError }}
        </div>
        <div v-if="loading" class="flex justify-center items-center py-12">
            <vue-loaders-ball-beat scale="2" color="black" />
        </div>

        <div v-else-if="error" class="alert alert-danger" role="alert">
            <strong>Literature could not be loaded.</strong>
            <span class="block mt-1">Please refresh the page and try again.</span>
        </div>

        <div v-else-if="literatures.length === 0" class="literature-empty p-8 text-center">
            <p class="mb-1 font-bold text-lg">No literature entries found</p>
            <p class="mb-0 text-sm text-pip-gray">Entries will appear here once they are available.</p>
        </div>

        <template v-else>
            <section class="literature-filters mb-3" aria-label="Literature filters">
                <div class="flex flex-col gap-2 lg:flex-row lg:items-end">
                    <div class="literature-search w-full">
                        <label for="literature-search" class="sr-only">Search literature</label>
                        <input
                            id="literature-search"
                            v-model.trim="searchQuery"
                            type="search"
                            class="form-control form-control-sm"
                            placeholder="Search name, category, or PDF…"
                        />
                    </div>
                    <div class="literature-filter-select">
                        <admin-form-field
                            id="literature-status-filter"
                            v-model="statusFilter"
                            label="Filter by status"
                            :options="statusFilterOptions"
                            hide-label
                        />
                    </div>
                    <div class="literature-filter-select">
                        <admin-form-field
                            id="literature-category-filter"
                            v-model="categoryFilter"
                            label="Filter by category"
                            :options="categoryFilterOptions"
                            hide-label
                        />
                    </div>
                    <div class="literature-filter-select">
                        <admin-form-field
                            id="literature-destination-filter"
                            v-model="destinationFilter"
                            label="Filter by publishing destination"
                            :options="destinationFilterOptions"
                            hide-label
                        />
                    </div>
                    <button v-if="hasActiveFilters" type="button" class="btn btn-sm btn-secondary flex-none" @click="clearFilters">
                        Clear filters
                    </button>
                </div>
            </section>

            <div
                v-if="filteredLiteratures.length"
                class="literature-toolbar flex items-center justify-between gap-3 mb-2"
            >
                <p class="mb-0 text-sm text-pip-gray" aria-live="polite">
                    <strong class="text-tertiary">{{ filteredLiteratures.length }}</strong>
                    {{ filteredLiteratures.length === 1 ? "item" : "items" }}
                    <span v-if="hasActiveFilters">of {{ literatures.length }}</span>
                    · Showing {{ rangeStart }}–{{ rangeEnd }}
                </p>
                <div class="flex items-center gap-2">
                    <label for="literature-page-size" class="mb-0 text-xs font-semibold text-pip-gray">Per page</label>
                    <admin-form-field
                        id="literature-page-size"
                        v-model="pageSize"
                        :options="pageSizeOptions"
                        class="literature-page-size"
                    />
                </div>
            </div>

            <div v-if="filteredLiteratures.length === 0" class="literature-empty p-6 text-center">
                <p class="mb-1 font-bold">No matching literature found</p>
                <p class="mb-3 text-sm text-pip-gray">Try changing your search or filters.</p>
                <button type="button" class="btn btn-sm btn-secondary" @click="clearFilters">Clear filters</button>
            </div>

            <template v-else>
                <div class="table-responsive literature-table-wrap">
                    <table class="table literature-table mb-0">
                        <thead>
                            <tr>
                                <th scope="col" class="column-cover">Cover</th>
                                <th scope="col" class="column-title">Literature</th>
                                <th scope="col">Category</th>
                                <th scope="col">Status</th>
                                <th scope="col">Start date</th>
                                <th scope="col">Published to</th>
                            </tr>
                        </thead>
                        <tbody>
                            <literature-table-row
                                v-for="lit in pageItems"
                                :key="lit.id"
                                :literature="lit"
                                :deleting="deletingId === lit.id"
                                @delete="requestDelete"
                            />
                        </tbody>
                    </table>
                </div>

                <div class="literature-pagination mt-3">
                    <jw-pagination
                        :key="paginationKey"
                        :items="filteredLiteratures"
                        :page-size="pageSize"
                        :initial-page="1"
                        :max-pages="7"
                        :disable-default-styles="true"
                        @changePage="onPageChange"
                    />
                </div>
            </template>
        </template>

        <admin-confirm-dialog
            id="portal-delete-literature"
            :open="Boolean(deleteCandidate)"
            title="Delete literature?"
            :message="deleteConfirmationMessage"
            confirm-label="Delete literature"
            working-label="Deleting…"
            :working="Boolean(deletingId)"
            @confirm="deleteLiterature"
            @cancel="deleteCandidate = null"
        />
    </div>
</template>

<script>
import JwPagination from "@c/shared/Pagination";
import { AdminConfirmDialog, AdminFormField } from "@c/shared/admin";
import LiteratureTableRow from "./literature/LiteratureTableRow";

export default {
    name: "LiteraturePortal",
    components: { AdminConfirmDialog, AdminFormField, JwPagination, LiteratureTableRow },
    data() {
        return {
            literatures: [],
            pageItems: [],
            pageSize: 10,
            currentPage: 1,
            searchQuery: "",
            statusFilter: "all",
            categoryFilter: "all",
            destinationFilter: "all",
            loading: false,
            error: null,
            deleteCandidate: null,
            deletingId: null,
            deleteError: null,
            deleteSuccessMessage: this.$route.query.deleted === "1"
                ? "Literature deleted successfully."
                : null,
        };
    },
    computed: {
        categoryOptions() {
            return [...new Set(this.literatures.map(lit => lit.catagory).filter(Boolean))].sort((a, b) =>
                a.localeCompare(b)
            );
        },
        statusFilterOptions() {
            return [
                { value: "all", label: "All statuses" },
                { value: "published", label: "Published" },
                { value: "draft", label: "Draft" },
                { value: "archived", label: "Archived" },
                { value: "unset", label: "Not set" },
            ];
        },
        categoryFilterOptions() {
            return [
                { value: "all", label: "All categories" },
                { value: "uncategorized", label: "Uncategorized" },
                ...this.categoryOptions.map(category => ({ value: category, label: category })),
            ];
        },
        destinationFilterOptions() {
            return [
                { value: "all", label: "Any destination" },
                { value: "retail", label: "Retail" },
                { value: "boss", label: "Boss" },
                { value: "catalog", label: "Catalog" },
                { value: "none", label: "Not published" },
            ];
        },
        pageSizeOptions() {
            return [10, 25, 50, 100].map(value => ({ value, label: String(value) }));
        },
        deleteConfirmationMessage() {
            if (!this.deleteCandidate) return "";
            return `“${this.deleteCandidate.name || "Untitled literature"}” will be permanently removed. This cannot be undone.`;
        },
        filteredLiteratures() {
            const query = this.searchQuery.toLowerCase();

            return this.literatures.filter(lit => {
                const matchesSearch =
                    !query ||
                    [lit.name, lit.catagory, lit.path].some(value =>
                        String(value || "").toLowerCase().includes(query)
                    );
                const normalizedStatus = String(lit.status || "").toLowerCase();
                const matchesStatus =
                    this.statusFilter === "all" ||
                    (this.statusFilter === "unset" ? !normalizedStatus : normalizedStatus === this.statusFilter);
                const matchesCategory =
                    this.categoryFilter === "all" ||
                    (this.categoryFilter === "uncategorized"
                        ? !lit.catagory
                        : lit.catagory === this.categoryFilter);
                const destinations = {
                    retail: this.isEnabled(lit.web_retail),
                    boss: this.isEnabled(lit.web_boss),
                    catalog: this.isEnabled(lit.web_cat),
                };
                const matchesDestination =
                    this.destinationFilter === "all" ||
                    (this.destinationFilter === "none"
                        ? !destinations.retail && !destinations.boss && !destinations.catalog
                        : destinations[this.destinationFilter]);

                return matchesSearch && matchesStatus && matchesCategory && matchesDestination;
            });
        },
        hasActiveFilters() {
            return Boolean(
                this.searchQuery ||
                    this.statusFilter !== "all" ||
                    this.categoryFilter !== "all" ||
                    this.destinationFilter !== "all"
            );
        },
        paginationKey() {
            return [
                this.pageSize,
                this.searchQuery,
                this.statusFilter,
                this.categoryFilter,
                this.destinationFilter,
                this.literatures.length,
            ].join("|");
        },
        rangeStart() {
            return this.pageItems.length ? (this.currentPage - 1) * this.pageSize + 1 : 0;
        },
        rangeEnd() {
            return (this.currentPage - 1) * this.pageSize + this.pageItems.length;
        },
    },
    created() {
        this.loading = true;
        axios
            .get(`/api/admin/literatures`)
            .then(response => {
                this.literatures = _.orderBy(response.data, "id");
            })
            .catch(error => {
                this.error = error;
            })
            .finally(() => {
                this.loading = false;
            });
    },
    methods: {
        onPageChange(pageOfItems) {
            this.pageItems = pageOfItems;
            // jw-paginate doesn't expose the current page number; derive it from the slice.
            if (pageOfItems.length && this.filteredLiteratures.length) {
                const firstIdx = this.filteredLiteratures.indexOf(pageOfItems[0]);
                this.currentPage = Math.floor(firstIdx / this.pageSize) + 1;
            }
        },
        clearFilters() {
            this.searchQuery = "";
            this.statusFilter = "all";
            this.categoryFilter = "all";
            this.destinationFilter = "all";
        },
        requestDelete(literature) {
            this.deleteError = null;
            this.deleteSuccessMessage = null;
            this.deleteCandidate = literature;
        },
        async deleteLiterature() {
            if (!this.deleteCandidate || this.deletingId) return;

            const literature = this.deleteCandidate;
            this.deletingId = literature.id;
            this.deleteError = null;

            try {
                await axios.delete(`/api/admin/literatures/${literature.id}`);
                this.literatures = this.literatures.filter(item => item.id !== literature.id);
                this.pageItems = this.pageItems.filter(item => item.id !== literature.id);
                this.deleteCandidate = null;
                this.deleteSuccessMessage = "Literature deleted successfully.";
            } catch (error) {
                this.deleteError = "Literature could not be deleted. Please try again.";
            } finally {
                this.deletingId = null;
            }
        },
        isEnabled(value) {
            return value === 1 || value === "1" || value === "y" || value === true;
        },
    },
};
</script>

<style scoped lang="scss">
.literature-empty {
    border: 1px solid var(--color-light-grey);
    background: #fff;
}

.literature-search {
    max-width: 360px;
}

.literature-filter-select {
    min-width: 9.5rem;
}

.literature-page-size {
    width: auto;
    min-width: 4.5rem;
}

.literature-table-wrap {
    border: 1px solid var(--color-light-grey);
}

.literature-table {
    min-width: 850px;
    color: var(--color-mine-shaft);
    font-size: var(--font-size-sm);
}

.literature-table th {
    padding: 0.55rem 0.65rem;
    border-top: 0;
    border-bottom: 1px solid var(--color-light-grey);
    background: var(--color-gallery);
    color: var(--color-mine-shaft);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-bold);
    letter-spacing: var(--tracking-wide);
    text-transform: uppercase;
    vertical-align: middle;
}

.column-cover {
    width: 4.5rem;
}

.column-title {
    width: 34%;
}

::v-deep .literature-pagination {
    .pagination {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
        gap: 0;
        margin: 0;
        padding: 0;
    }

    .page-item {
        list-style: none;
    }

    .page-link {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 2.35rem;
        height: 2.35rem;
        margin: 0;
        padding: 0 0.7rem;
        border: 1px solid #d2d2d2;
        border-left: 0;
        border-radius: 0 !important;
        background: #fff;
        color: var(--color-mine-shaft);
        cursor: pointer;
        font-family: "Acumin Pro", Arial, Helvetica, sans-serif;
        font-size: 0.75rem;
        font-weight: 700;
        line-height: 1;
        text-transform: none;
        transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
    }

    .page-item:first-child .page-link {
        border-left: 1px solid #d2d2d2;
    }

    .page-link:focus-visible {
        position: relative;
        z-index: 1;
        outline: 2px solid rgba(205, 27, 0, 0.45);
        outline-offset: -2px;
    }

    .page-link i {
        font-size: 0.7rem;
    }

    .page-item:not(.active):not(.disabled) .page-link:hover {
        border-color: #aaa;
        background: #f3f3f3;
        color: #cd1b00;
    }

    .page-item.active .page-link {
        border-color: #cd1b00;
        background: #cd1b00;
        color: #fff;
    }

    .page-item.disabled .page-link {
        border-color: #ddd;
        background: #f7f7f7;
        color: #9ca3af;
        cursor: not-allowed;
        opacity: 1;
    }
}

@media (max-width: 1023.98px) {
    .literature-search {
        max-width: none;
    }

    .literature-filter-select {
        width: 100%;
    }
}
</style>
